import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { websiteApi } from '../api/website.api';
import { useNotify } from '../context/NotifyContext';

interface NewsletterSubscribersPanelProps {
  websiteId: string;
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const NewsletterSubscribersPanel: React.FC<NewsletterSubscribersPanelProps> = ({ websiteId }) => {
  const queryClient = useQueryClient();
  const { notify, confirm } = useNotify();
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data, isLoading } = useQuery({
    queryKey: ['newsletterSubscribers', websiteId, page],
    queryFn: () => websiteApi.getNewsletterSubscribers(websiteId, page, limit),
    enabled: !!websiteId,
  });

  const deleteMutation = useMutation({
    mutationFn: (subscriberId: string) => websiteApi.deleteNewsletterSubscriber(websiteId, subscriberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsletterSubscribers', websiteId] });
    },
    onError: (error: any) => {
      notify.error(error.response?.data?.message || 'Failed to remove subscriber');
    },
  });

  const handleDelete = async (subscriberId: string) => {
    const ok = await confirm({
      title: 'Remove subscriber',
      message: 'Remove this subscriber? This cannot be undone.',
      confirmLabel: 'Remove',
      danger: true,
    });
    if (ok) deleteMutation.mutate(subscriberId);
  };

  const handleCopyAllEmails = () => {
    if (!data?.items.length) return;
    const emails = data.items.map((s) => s.email).join(', ');
    navigator.clipboard?.writeText(emails).then(
      () => notify.success('Emails copied to clipboard.'),
      () => notify.error('Could not copy emails. Please select and copy manually.')
    );
  };

  return (
    <div className="nova-card p-4 sm:p-6">
      <div className="flex flex-wrap justify-between items-center gap-2 mb-6">
        <h2 className="text-2xl font-bold text-nova-text">Newsletter Subscribers</h2>
        {!!data?.total && (
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 text-sm font-semibold rounded-full bg-nova-sky/10 text-nova-sky">
              {data.total} subscriber{data.total === 1 ? '' : 's'}
            </span>
            <button
              onClick={handleCopyAllEmails}
              className="px-3 py-1.5 text-sm border border-nova-border rounded-lg text-nova-text2 hover:bg-nova-bg2 transition-colors"
            >
              Copy all emails (this page)
            </button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-nova-sky"></div>
        </div>
      ) : !data || data.items.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-nova-text3">
            No subscribers yet. Add a "Newsletter" section to your website so visitors can sign up.
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {data.items.map((subscriber) => (
              <div
                key={subscriber._id}
                className="border border-nova-border rounded-lg p-3 flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <p className="font-medium text-nova-text truncate">{subscriber.email}</p>
                  <p className="text-xs text-nova-text3">{formatDate(subscriber.createdAt)}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <a
                    href={`mailto:${subscriber.email}`}
                    className="text-nova-sky hover:text-nova-sky2 text-sm font-medium"
                  >
                    Email
                  </a>
                  <button
                    onClick={() => handleDelete(subscriber._id)}
                    className="text-nova-red hover:text-nova-red/80 text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          {data.totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-6">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 border border-nova-border text-nova-text2 rounded-lg hover:bg-nova-bg2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-nova-text3">
                Page {data.page} of {data.totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                disabled={page >= data.totalPages}
                className="px-3 py-1.5 border border-nova-border text-nova-text2 rounded-lg hover:bg-nova-bg2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NewsletterSubscribersPanel;
